/** Server-Verwaltung (admin): Liste + Anlegen/Bearbeiten/Löschen. */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api } from "../store";
import { PageHead, Panel } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";

interface PubServer { id: string; name: string; host: string; port: number; hasPassword: boolean }
interface Draft { id: string; name: string; host: string; port: string; password: string }

export function Servers() {
  const list = useSignal<PubServer[]>([]);
  const draft = useSignal<Draft | null>(null);
  const edit = useSignal(false);

  async function load() {
    const r = await api.call<ApiResponse & { servers: PubServer[] }>("server_list");
    if (r.ok) list.value = r.servers;
  }
  useSignalEffect(() => { void load(); });

  const newServer = () => (draft.value = { id: "", name: "", host: "", port: "4711", password: "" });
  const editServer = (s: PubServer) => (draft.value = { id: s.id, name: s.name, host: s.host, port: String(s.port), password: "" });

  async function save() {
    const d = draft.value;
    if (!d) return;
    const r = await api.call<ApiResponse>("server_save", { id: d.id, name: d.name, host: d.host, port: Number(d.port), password: d.password });
    if (r.ok) { toast(t("common.saved"), "ok"); draft.value = null; await load(); }
    else toast(String(r.error ?? t("common.error")), "err");
  }
  async function del(s: PubServer) {
    if (!confirm(`${t("servers.confirmDeletePre")}${s.name}${t("servers.confirmDeletePost")}`)) return;
    const r = await api.call<ApiResponse>("server_delete", { id: s.id });
    if (r.ok) { toast(t("common.deleted"), "ok"); await load(); } else toast(String(r.error ?? t("common.error")), "err");
  }

  const d = draft.value;
  const setD = (patch: Partial<Draft>) => { if (d) draft.value = { ...d, ...patch }; };

  const items: PanelItem[] = [
    { id: "list", title: t("view.servers"), defaultSpan: 8, render: () => (
      <Panel title={t("view.servers")}>
        <table class="score">
          <thead><tr><th>Name</th><th>Host</th><th class="num">Port</th><th>RCON-PW</th><th></th></tr></thead>
          <tbody>
            {list.value.map((s) => (
              <tr key={s.id}>
                <td data-label="Name">{s.name}<div class="caption mono">{s.id}</div></td>
                <td class="mono" data-label="Host">{s.host}</td>
                <td class="num" data-label="Port">{s.port}</td>
                <td data-label="RCON-PW">{s.hasPassword ? <span class="badge accent">{t("servers.pwSet")}</span> : <span class="badge">{t("servers.pwMissing")}</span>}</td>
                <td class="num">
                  <button class="btn sm ghost" onClick={() => editServer(s)}>{t("common.editAction")}</button>
                  <button class="btn sm ghost" onClick={() => void del(s)}>{t("common.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    ) },
    ...(d ? [{ id: "editor", title: d.id ? `„${d.name || d.id}" ${t("servers.editSuffix")}` : t("servers.newServer"), defaultSpan: 4, render: () => (
      <Panel title={d.id ? `„${d.name || d.id}" ${t("servers.editSuffix")}` : t("servers.newServer")} actions={<button class="btn sm ghost" onClick={() => (draft.value = null)}>✕</button>}>
        <div class="stack">
          <div class="field"><label>Name</label><input class="input" value={d.name} onInput={(e) => setD({ name: (e.target as HTMLInputElement).value })} /></div>
          <div class="field"><label>{t("servers.hostIp")}</label><input class="input mono" value={d.host} onInput={(e) => setD({ host: (e.target as HTMLInputElement).value })} /></div>
          <div class="field"><label>Port</label><input class="input mono" type="number" value={d.port} onInput={(e) => setD({ port: (e.target as HTMLInputElement).value })} /></div>
          <div class="field"><label>{t("servers.rconPassword")} {d.id && <span class="faint">{t("servers.emptyUnchanged")}</span>}</label>
            <input class="input" type="password" autocomplete="new-password" value={d.password} onInput={(e) => setD({ password: (e.target as HTMLInputElement).value })} /></div>
          {!d.id && <div class="caption">{t("servers.idAutoHint")}</div>}
          <button class="btn primary" onClick={() => void save()}>{t("common.save")}</button>
        </div>
      </Panel>
    ) }] as PanelItem[] : []),
  ];

  return (
    <div class="view-enter">
      <PageHead title={t("view.servers")} sub={t("servers.sub")}>
        <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
        <button class="btn primary" onClick={newServer}>{t("servers.addServer")}</button>
      </PageHead>
      <PanelGrid view="servers" items={items} edit={edit} />
    </div>
  );
}
